'use client'

import type React from 'react'
import type { ClientUploadedFileData } from 'uploadthing/types'
import {
  AttachmentLink,
  ManageAttachment,
  UPLOAD_ENDPOINT,
  UploadFilesButton
} from '@/frontend/design-system/actions/Attachments'
import { P2 } from '@/frontend/design-system/typography/Paragraph'

export type DbAttachment = {
  name: string
  url: string
}

export const mapAttachmentsToFiles = (
  attachments?: DbAttachment[]
): ClientUploadedFileData<{ result: string }>[] => {
  return (attachments ?? []).map(att => ({
    key: att.name,
    name: att.name,
    ufsUrl: att.url,
    url: att.url,
    appUrl: att.url,
    fileHash: att.name,
    serverData: { result: att.url },
    type: 'file',
    size: 0,
    lastModified: Date.now(),
    customId: null
  }))
}

type AttachmentListProps = {
  files: ClientUploadedFileData<{ result: string }>[]
  setFiles: (files: ClientUploadedFileData<{ result: string }>[]) => void
  disabled?: boolean
}

const ManageAttachmentsList: React.FC<AttachmentListProps> = ({
  files,
  setFiles,
  disabled = false
}) => {
  return (
    <div className='flex w-full flex-col gap-2'>
      {files.map(file => (
        <ManageAttachment
          key={file.key}
          file={file}
          files={files}
          setFiles={setFiles}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

export const AttachmentLinksList: React.FC<{
  files: ClientUploadedFileData<{ result: string }>[]
}> = ({ files }) => {
  if (files.length === 0) return null
  return (
    <div className='flex flex-col gap-2'>
      {files.map(file => (
        <AttachmentLink key={file.key} attachment={{ name: file.name, url: file.ufsUrl }} />
      ))}
    </div>
  )
}

type AttachmentsManagerProps = {
  files: ClientUploadedFileData<{ result: string }>[]
  setFiles: (files: ClientUploadedFileData<{ result: string }>[]) => void
  singleView?: boolean
  preview?: boolean
  disabled?: boolean
}

export const AttachmentsManager: React.FC<AttachmentsManagerProps> = ({
  files,
  setFiles,
  singleView = false,
  preview = false,
  disabled = false
}) => {
  const handleUpload = (uploaded: ClientUploadedFileData<{ result: string }>[]) => {
    setFiles([...files, ...uploaded])
  }

  if (singleView) {
    return (
      <div className='flex flex-col gap-2'>
        {preview ? (
          <AttachmentLinksList files={files} />
        ) : (
          <div className='flex flex-col items-start gap-2'>
            <UploadFilesButton
              endpoint={UPLOAD_ENDPOINT.ATTACHMENT}
              setFiles={handleUpload}
              disabled={disabled}
            />
            {files.length === 0 ? (
              <P2 className='text-contentTertiary'>Nie dołączono żadnych plików.</P2>
            ) : (
              <ManageAttachmentsList files={files} setFiles={setFiles} disabled={disabled} />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='flex gap-4'>
      <div className='flex flex-1 flex-col gap-2'>
        <div className='flex items-center gap-3'>
          <UploadFilesButton
            endpoint={UPLOAD_ENDPOINT.ATTACHMENT}
            setFiles={handleUpload}
            disabled={disabled}
          />
          {files.length === 0 && (
            <P2 className='text-contentSecondary'>Nie dołączono żadnych plików.</P2>
          )}
        </div>
        {files.length > 0 && (
          <ManageAttachmentsList files={files} setFiles={setFiles} disabled={disabled} />
        )}
      </div>

      <div className='flex flex-1 flex-col gap-2'>
        <AttachmentLinksList files={files} />
      </div>
    </div>
  )
}
